import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { browserHistory } from 'react-router';

import auth from './auth';
import ajax from './ajax';
import * as enums from '@playbook-technologies/common-enums';

import { getInitials, getRandomColor, getRatingStars } from './helpers';
import Header from './Header';
import Spinner from './Spinner';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import * as styles from './WorkoutCommentList.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { CommentApi } from '../api/CommentApi';
import { ButtonLink } from '@design-system/components/button/button';
import { faReply } from '@fortawesome/pro-solid-svg-icons';
import { PATHS } from '@client/routes';
import toast from 'react-hot-toast';

const getStatusColor = (status) => {
  switch (status) {
    case enums.Post.Status.Seen.value:
      return '#81b3bf';
    case enums.Post.Status.Reported.value:
      return '#f19041';
    case enums.Post.Status.Archived.value:
      return '#2c2c2c';
    case enums.Post.Status.New.value:
      return '#81b3bf';
    default:
      return null;
  }
};

const getWorkoutStatus = (status) => {
  switch (status) {
    case enums.Post.Status.Reported.value:
      return 'Reported';
    case enums.Post.Status.Archived.value:
      return 'Hidden';
    case enums.Post.Status.Seen.value:
      return 'Marked safe';
    case enums.Post.Status.New.value:
      return 'Visible';
    default:
      return 'Unknown status';
  }
};

const ClickableIcon = ({ isActive, color, icon, ...rest }) => (
  <FontAwesomeIcon
    icon={icon}
    className={styles.clickableIcon({ isActive: !!isActive })}
    style={assignInlineVars({ [styles.iconColorVar]: color })}
    {...rest}
  />
);

const CommentModeration = ({ commentId, status }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [commentStatus, setCommentStatus] = useState(status);
  const isDisplayed =
    commentStatus === enums.Post.Status.New.value ||
    commentStatus === enums.Post.Status.Seen.value;
  const isHidden = commentStatus === enums.Post.Status.Archived.value;

  return (
    <div className={styles.moderationContainer}>
      {isUpdating && <Spinner color={`#6367ef`} size={15} duration={600} />}
      <span
        className={styles.status}
        style={assignInlineVars({
          [styles.statusColorVar]: getStatusColor(commentStatus),
        })}
      >
        {getWorkoutStatus(commentStatus)}
      </span>
      <ClickableIcon
        size="lg"
        isActive={isDisplayed}
        color={'#488a37'}
        icon={faCheckCircle}
        onClick={() => {
          if (!isDisplayed) {
            setIsUpdating(true);
            return CommentApi.keepComment(commentId)
              .then(() => setCommentStatus(enums.Post.Status.Seen.value))
              .finally(() => setIsUpdating(false));
          }
        }}
      />
      <ClickableIcon
        size="lg"
        isActive={isHidden}
        color={'#9e3834'}
        icon={faTimesCircle}
        onClick={() => {
          if (!isHidden) {
            setIsUpdating(true);
            return CommentApi.hideComment(commentId)
              .then(() => setCommentStatus(enums.Post.Status.Archived.value))
              .finally(() => setIsUpdating(false));
          }
        }}
      />
    </div>
  );
};

export const WorkoutCommentList = (props) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const queryParams = new URLSearchParams(props.location?.search);
  const programName = queryParams.get('programName');

  useEffect(() => {
    try {
      setLoading(true);
      ajax
        .fetchWorkoutComments(props.params.id)
        .then((workoutComments: any) => {
          setItems(workoutComments);
        });
    } catch (err) {
      toast.error('Workout comments not fetched, please refresh page.');
    } finally {
      setLoading(false);
    }
  }, []);

  const dmEnabled = true; // todo get from customization flag

  return (
    <div className="portalUserList p-5 md:p-10">
      <div className="workoutList">
        <main id="main">
          <Header>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '17px 0',
              }}
            >
              <button
                className="button green-button"
                onClick={() => browserHistory.goBack()}
              >
                Go back
              </button>
            </div>
          </Header>
          <div>
            <br />
            <section className="settings feed">
              {loading && (
                <Spinner color={`#6367ef`} style={{ 'margin-left': '50%' }} />
              )}
              {!loading &&
                !items.length &&
                'No comments found for given workout'}
              {items &&
                items.length > 0 &&
                items.map((p, index) => {
                  return (
                    <WorkoutCommentItem
                      key={index}
                      status={p.status}
                      id={p.id}
                      user={p.user}
                      workout={p.workout}
                      programName={programName}
                      influencer={p.influencer}
                      userActivity={p.userActivity}
                      type={p.type}
                      textValue={p.textValue}
                      createdAt={p.createdAt}
                      dmEnabled={dmEnabled}
                    />
                  );
                })}
              <br />
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

const Username = ({ user }) => {
  const name = typeof user?.name === 'string' ? user.name.trim() : '';

  if (!user || name.length === 0) {
    return 'Anonymous';
  }
  if (user.email) {
    return <a href={`mailto:${user.email}`}>{name}</a>;
  }
  return name;
};

const WorkoutCommentItem = ({
  id,
  user,
  type,
  workout,
  programName,
  influencer,
  textValue,
  userActivity,
  status,
  createdAt,
  dmEnabled,
}) => {
  const instagram =
    user && user.instagram && user.instagram.trim().length > 0 ? (
      <a
        style={{ margin: '0 2px 0 0', color: '#fff' }}
        target="_blank"
        href={`https://instagram.com/${user.instagram}`}
      >
        <span
          className="icon-instagram"
          style={{ padding: '2px', borderRadius: '2px' }}
        ></span>
      </a>
    ) : (
      ''
    );

  let thumbnail = getThumbnail(
    user ? user.name : null,
    user ? user.mediaThumbnail : null,
    getRandomColor(),
  );
  const when = moment(createdAt).fromNow();

  var metadata = '';
  const inboxPath = auth.isInfluencer()
    ? PATHS.INBOX(user.id)
    : PATHS.INBOX_MESSAGE(influencer.id, user.id);
  if (type === enums.Post.Type.WorkoutComment.value) {
    if (!workout.isProtected) metadata += ' (trial)';
    if (influencer && !auth.isInfluencer())
      metadata += ` by ${influencer.name}`;
    return (
      <div
        style={{ borderBottom: '1px solid #ddd', padding: '15px 5px' }}
        key={id}
      >
        {thumbnail}

        <div className="flex flex-col flex-end float-right">
          {dmEnabled && (
            <ButtonLink
              to={inboxPath}
              variant="outlined"
              color="neutral"
              className="flex gap-1 items-center"
            >
              <FontAwesomeIcon icon={faReply} />
              reply via dm
            </ButtonLink>
          )}
          {(auth.isModerator() || auth.isAdministrator()) && (
            <CommentModeration commentId={id} status={status} />
          )}
        </div>

        <div>
          <Username user={user} /> {instagram}{' '}
          <span className="text-[#aaa] text-sm">{when}</span>
        </div>
        <div
          style={{
            margin: '3px 5px 3px 0',
            fontSize: '18px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <span className="stars">
            {getRatingStars(userActivity.floatValue)}
          </span>
          <span className="text-[#aaa] text-sm">
            {workout.name}
            {programName ? ` • ${programName}` : ''}
            {metadata}:
          </span>
          <span style={{ paddingTop: '3px' }}>{textValue}</span>
        </div>
      </div>
    );
  } else {
    if (influencer && !auth.isInfluencer()) {
      metadata += ` to ${influencer.name}`;
    }
    var verb = '';
    if (type === enums.Post.Type.Feedback.value) verb = 'posted feedback';
    else if (type === enums.Post.Type.InsightSuggestion.value)
      verb = 'suggested insight video';
    else if (type === enums.Post.Type.WorkoutSuggestion.value)
      verb = 'suggested workout';
    else if (type === enums.Post.Type.Question.value) verb = 'posted question';
    else if (type === enums.Post.Type.Testimonial.value)
      verb = 'posted testimonial';
    return (
      <div style={{ borderBottom: '1px solid #ddd', padding: '5px' }} key={id}>
        {thumbnail}
        <div style={{ float: 'right', color: '#aaa' }}>{when}</div>
        <div>
          <Username user={user} /> {instagram}
          <strong>{verb}</strong>
          {metadata}:
        </div>
        <p style={{ margin: '3px 0', fontSize: '18px' }}>{textValue}</p>
      </div>
    );
  }
};

function getThumbnail(name, mediaThumbnail, color) {
  if (mediaThumbnail) {
    return (
      <div
        style={{
          float: 'left',
          width: '40px',
          height: '40px',
          marginRight: '5px',
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      >
        <img
          src={mediaThumbnail.cdn01 + mediaThumbnail.image}
          style={{ width: '40px', height: '40px' }}
        />
      </div>
    );
  } else {
    return (
      <div
        style={{
          float: 'left',
          width: '40px',
          height: '40px',
          marginRight: '5px',
          borderRadius: '20px',
          borderStyle: 'solid',
          borderWidth: '3px',
          borderColor: color ? color : '#000',
        }}
      >
        <span
          style={{
            width: '34px',
            textAlign: 'center',
            lineHeight: '34px',
            display: 'inline-block',
            color: color ? color : '#000',
          }}
        >
          {getInitials(name)}
        </span>
      </div>
    );
  }
}
